import { useQuery } from '@tanstack/react-query';
import clsx from 'clsx';
import { PhoneCall, PhoneIncoming, PhoneOutgoing } from 'lucide-react';
import { useEffect, useState } from 'react';
import { get } from '../lib/api';
import { callPartyLabel, formatDuration, formatPhone, statusLabel } from '../lib/format';
import { useContactHistory } from '../lib/queries';
import { CallView } from '../lib/types';
import { useSoftphone } from '../app/softphone';
import { SoftphoneCallBar } from './SoftphoneCallBar';
import { Badge, Button } from './ui';

/**
 * Ligações em andamento do operador (screen-pop): tocando no ramal dele ou em ligação.
 * Mostra quem é, o cliente e os atendimentos em aberto do contato. Atender/desligar é no
 * telefone/softphone por enquanto; no navegador (WebRTC) na entrega 2.2.
 */
export function CallPanel({ onOpenAttendance }: { onOpenAttendance: (id: string) => void }) {
  const { data } = useQuery({ queryKey: ['calls', 'active'], queryFn: () => get<CallView[]>('/calls/active'), refetchInterval: 30_000 });
  const calls = (data ?? []).filter((c) => c.state !== 'ended');
  const { call: browserCall } = useSoftphone();
  if (calls.length === 0 && !browserCall) return null;
  return (
    <div className="fixed inset-x-2 top-16 z-40 flex flex-col gap-2 md:inset-x-auto md:right-4 md:w-80">
      <SoftphoneCallBar />
      {calls.map((c) => (
        <CallCard key={c.id} call={c} onOpenAttendance={onOpenAttendance} />
      ))}
    </div>
  );
}

function useNow(): number {
  const [now, setNow] = useState(Date.now());
  useEffect(() => {
    const t = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(t);
  }, []);
  return now;
}

function CallCard({ call, onOpenAttendance }: { call: CallView; onOpenAttendance: (id: string) => void }) {
  const now = useNow();
  const history = useContactHistory(call.contact?.id ?? null).data ?? [];
  const open = history.filter((h) => h.status !== 'finalizado' && h.id !== call.attendanceId);
  const ringing = call.state === 'ringing';
  const since = Math.max(0, Math.floor((now - Date.parse(call.answeredAt ?? call.startedAt)) / 1000));
  const Icon = call.direction === 'outbound' ? PhoneOutgoing : ringing ? PhoneIncoming : PhoneCall;

  return (
    <div className={clsx('rounded-lg border bg-panel p-3 shadow-lg', ringing ? 'border-accent ring-2 ring-accent/30' : 'border-border')}>
      <div className="flex items-start gap-2.5">
        <span className={clsx('mt-0.5 rounded-full p-1.5', ringing ? 'animate-pulse bg-accent text-accent-fg' : 'bg-ok/15 text-ok')}>
          <Icon className="size-4" />
        </span>
        <div className="min-w-0 flex-1">
          <div className="flex items-center justify-between gap-2 text-xs text-fg-soft">
            <span>
              {ringing ? (call.direction === 'outbound' ? 'Chamando…' : 'Tocando') : 'Em ligação'}
              {call.ringingForMe && call.ringing.length > 0 && <> · ramal {call.ringing.join(', ')}</>}
              {!ringing && call.extension && <> · ramal {call.extension}</>}
            </span>
            <span className="font-mono">{formatDuration(since)}</span>
          </div>
          <div className="truncate font-semibold">{callPartyLabel(call)}</div>
          {call.contact && call.phone && <div className="text-xs text-fg-soft">{formatPhone(call.phone)}</div>}
          {call.contact?.customer && <div className="truncate text-xs">{call.contact.customer.name}</div>}
          {call.phone && !call.contact && <div className="text-xs text-fg-soft">Número sem cadastro na plataforma</div>}
        </div>
      </div>

      {call.attendanceId && call.state === 'answered' && (
        <Button size="sm" className="mt-2 w-full" onClick={() => onOpenAttendance(call.attendanceId!)} title="O tempo da ligação conta neste atendimento">
          Abrir o atendimento da ligação
        </Button>
      )}
      {open.length > 0 && (
        <div className="mt-2 border-t border-border pt-2">
          <div className="mb-1 text-[11px] font-semibold tracking-wide text-fg-soft uppercase">Atendimentos em aberto</div>
          {open.slice(0, 3).map((h) => (
            <button key={h.id} className="flex w-full items-center justify-between gap-2 rounded px-1.5 py-1 text-left text-xs hover:bg-muted" onClick={() => onOpenAttendance(h.id)}>
              <span className="truncate">{h.ticketCode ?? (h.ticketId ? `#${h.ticketId}` : 'sem chamado')} · {h.operatorName ?? 'na fila'}</span>
              <Badge>{statusLabel(h)}</Badge>
            </button>
          ))}
        </div>
      )}
      {call.contact && open.length === 0 && history.length > 0 && (
        <button className="mt-2 w-full rounded px-1.5 py-1 text-left text-xs text-accent hover:bg-muted" onClick={() => onOpenAttendance(history[0].id)}>
          Ver o último atendimento deste cliente
        </button>
      )}
    </div>
  );
}
